/* rt-pack-axis-hint.js — Complete Package variant pickers.
 *
 * A pack that spans two rim diameters cannot have a complete option grid: an 18" tire has no
 * variant on a 20" rim. Stock Dawn renders those as "<value> - Unavailable", leaves them
 * selectable, and keeps the OLD price in the buy box — live on 2026-08-27 a Fuel Contra pack
 * showed $2,000 with 20x10 -18mm selected, against a real $2,260.
 *
 * So: a combination that does not exist is greyed out and SAYS WHAT TO PICK INSTEAD —
 *     LT 275/70R18 — select an 18" rim
 *     20x9 +2mm — select a 20" tire
 * with the size read off the entry itself, so a 22" tire says `select a 22" rim` unprompted.
 * RT 2026-08-27: "rather than 'unavailable' could it say something like - select a 20" rim".
 *
 * Ships as an asset, not inline Liquid: a custom_liquid block on product.pkg renders in the
 * HTML but does not reliably reach the DOM.
 */
(function () {
  'use strict';
  if (window.rtPackAxisHintWired) return;
  window.rtPackAxisHintWired = true;

  var HINT = ' — select ';          // our own marker, so a re-label is reversible

  /* ---- TREAD DEPTH ON EACH ROW (RT 2026-08-30) ---------------------------------------
   * RT: "does it make more sense to have it right on the lines of each tire size? rather
   * than the separate chip?" — yes, because the OPEN list is the only place all the depths
   * are visible at once, and comparing 12.5 vs 16 vs 17 IS the sale.
   *
   * Source is `custom.tread_by_option`, rendered as inert JSON by snippets/pack-spec-strip
   * (the option VALUES are deliberately untouched: they print verbatim into Kijiji and FB and
   * feed build_pack_specs' size parser, and lengthening them re-wraps the closed control on a
   * phone — the exact defect the grouping exists to remove).
   * A row with no verified depth simply gets nothing ([[absent-is-not-unreadable]]).
   */
  var TREAD = null;
  function treadMap() {
    if (TREAD !== null) return TREAD;
    TREAD = false;
    var tag = document.getElementById('rt-tread-map');
    if (tag) { try { TREAD = JSON.parse(tag.textContent) || false; } catch (e) { TREAD = false; } }
    return TREAD;
  }
  function treadOf(value) {
    var m = treadMap();
    return (m && Object.prototype.hasOwnProperty.call(m, value)) ? m[value] : '';
  }

  // EVERY DOM WRITE BELOW MUST BE CONDITIONAL. fix() is driven by a MutationObserver watching
  // {childList, subtree} on <variant-selects>, and `o.textContent = t` replaces the option's
  // text node even when the string is IDENTICAL — a childList mutation, which re-fires the
  // observer, which calls fix() again. Forever. Measured live 2026-08-27 on the AMP Pro 18"/20"
  // pack: one identical write cascaded into 71 more mutations. setTimeout(fn, 0) runs at the
  // ~4ms floor in a FOREGROUND tab, so the 7 greyed options were rebuilt ~250x/second and Chrome
  // rebuilt the open native dropdown with them — the cursor flickered non-stop over a greyed row
  // (RT). A hidden tab clamps timers to ~1s and disguises this as a harmless 1Hz tick.
  // NOTE these guards test the CURRENT DOM STATE, which is NOT the mistake described below:
  // that one gated the undo on finding our own label. End state here is byte-identical either
  // way; only the redundant write is skipped.
  function setText(o, t) { if (o.textContent !== t) o.textContent = t; }
  function setFlag(o, k, v) { if (o[k] !== v) o[k] = v; }
  var lastAdvance = 0, burst = 0;

  function diameter(value) {
    var m = value.match(/R(\d{2})\b/);   // LT 275/70R18, LT 33x12.5R20, LT275/65R18
    if (m) return { d: m[1], kind: 'tire' };
    m = value.match(/^(\d{2})x/);        // 20x9 +2mm, 18x9 -12 mm
    if (m) return { d: m[1], kind: 'rim' };
    return null;                         // not a size axis - leave this picker alone
  }

  function labelFor(value, info) {
    var article = /^(8|11|18)/.test(info.d) ? 'an' : 'a';
    var want = info.kind === 'tire' ? 'rim' : 'tire';
    return value + HINT + article + ' ' + info.d + '" ' + want;
  }

  function variantsOf(host) {
    var tag = host.querySelector('script[type="application/json"]');
    if (!tag) return null;
    try {
      var data = JSON.parse(tag.textContent);
      return (data && data.length) ? data : null;
    } catch (e) { return null; }
  }

  /* ---- combined-axis GROUPING -------------------------------------------------------
   * A merged pack sells one row per real combination on ONE axis:
   *     17x7 (+45mm) + 215/50R17
   *     17x7 (+45mm) + 215/55R17   ...
   * Every row repeats the rim, which is what makes an 18-row dropdown read as noise.
   * RT 2026-08-27: "just have a heading with 17x7 and then the sizes under it".
   *
   * So the rim becomes an <optgroup> LABEL and each option shows only its tire size. This is
   * DISPLAY ONLY — option.value keeps the full "rim + tire" string, which is what Dawn matches
   * a variant on, and what [[pack-listing-names-every-rim-size]] requires the VALUE to carry.
   * The rim is still on screen for every row, factored up into the heading above it.
   *
   * Only fires on a SINGLE-select picker, so it can never interleave with the hint pass below
   * (that one needs 2+ selects). Reads o.value, never o.textContent, so it is stable under
   * Dawn's text-patching re-render.
   */
  var SPLIT = ' + ';
  var CAN_STYLE = !!(window.CSS && CSS.supports && CSS.supports('appearance', 'base-select'))
      && !/[?&]rtfb=1/.test(location.search);   // ?rtfb=1 forces the fallback branch for testing

  /* Safari and Firefox have no customizable select, so the closed control just mirrors the
   * SELECTED option's text and there is no way to give it different content. Showing the full
   * "rim + tire" on every row is what RT rejected on mobile 2026-08-27 ("mobile is not good" —
   * every row wrapped onto two lines). So on those browsers: the SELECTED row carries the full
   * label (which is what the closed box shows) and every other row carries just the tire size,
   * with the optgroup heading above supplying the rim. iOS renders optgroup labels as section
   * headers, so the grouping still reads correctly there.
   * Writes are conditional, so re-running this is a no-op and cannot feed the observer.
   */
  /* Decide ONCE per picker whether the rim's parenthetical may leave the row.
   * It may ONLY when every rim group carries the SAME one (or none). Where they differ
   * (+44 vs +47, +35 vs +45) that parenthetical is what tells the rims apart, and dropping it
   * would park a fitment qualifier in a heading iOS renders grey and unstyleable — exactly the
   * failure [[never-shorten-a-label-into-a-claim]] describes ("trim the REDUNDANT word, never
   * the QUALIFIER"). Measured 2026-08-27 across all 52 live combined-axis packs: only 4 carry a
   * uniform parenthetical, 14 genuinely differ, the rest have none.
   * Fails CLOSED — anything unparseable keeps the full untouched label.
   */
  function uniformParen(sel) {
    var seen = null;
    for (var i = 0; i < sel.options.length; i++) {
      var v = sel.options[i].value, at = v.indexOf(SPLIT);
      if (at < 0) return false;
      var m = v.slice(0, at).match(/\(([^)]*)\)\s*$/);
      var paren = m ? m[1] : '';
      if (seen === null) seen = paren;
      else if (seen !== paren) return false;
    }
    return seen !== null;
  }

  /* Separator stays SPLIT (" + "), never "·": on this page "·" already means ALTERNATIVES
   * ("Rim Size 17x7 · 18x8 · 19x8"), so "17x7 · 215/50R17" would read as "or". */
  function rowLabel(o, drop) {
    var v = o.value, at = v.indexOf(SPLIT);
    var d = treadOf(v);
    if (at < 0) return d ? v + ' \u00b7 ' + d : v;
    var rim = v.slice(0, at);
    if (drop) rim = rim.replace(/\s*\([^)]*\)\s*$/, '');
    rim = rim.replace(/\s+$/, '');
    if (!rim) return v;
    return rim + SPLIT + v.slice(at + SPLIT.length) + (d ? ' \u00b7 ' + d : '');
  }

  function refreshLabels(sel) {
    var drop = uniformParen(sel);
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      var want = o.selected ? o.value : rowLabel(o, drop);
      if (o.textContent !== want) o.textContent = want;
    }
  }

  /* Chrome renders <optgroup> labels greyed and small — the OPPOSITE emphasis to what a heading
   * wants (RT 2026-08-27: "they should be larger and dark bolded font"). A native select popup
   * cannot be styled at all, so this only takes effect where `appearance: base-select` is
   * supported (Chromium 135+); elsewhere the @supports block is skipped entirely and the picker
   * stays native and untouched. Scoped to `select.rt-grouped`, so nothing else on the site moves.
   */
  function injectStyle() {
    if (document.getElementById('rt-pack-axis-style')) return;
    var st = document.createElement('style');
    st.id = 'rt-pack-axis-style';
    st.textContent =
      '@supports (appearance: base-select){' +
            /* base-select makes the control a FLEX box with align-items:normal, so <selectedcontent>
       * stretches to the full 44px while its line box is 25.65px — the label sits hard against
       * the top with ~18px dead space under it (RT 2026-08-27: "the size inside the box isnt
       * centered"). align-items:center lets it take its natural 28px; width:100% makes the
       * control fill Dawn's wrapper so the picker icon sits at the right edge. */
      'variant-selects select.rt-grouped{appearance:base-select;text-align:left;' +
        'align-items:center;width:100%;}' +
      'variant-selects select.rt-grouped::picker(select){appearance:base-select;background:#fff;' +
        'border:1px solid #d7dbdf;border-radius:12px;box-shadow:0 14px 36px rgba(0,0,0,.20);padding:6px;}' +
      'variant-selects select.rt-grouped optgroup{font-size:17px;font-weight:800;color:#11161c;' +
        'padding:12px 10px 6px;}' +
      'variant-selects select.rt-grouped option{font-size:15px;font-weight:500;color:#1a1a1a;' +
        'padding:8px 10px 8px 24px;border-radius:8px;}' +
      'variant-selects select.rt-grouped option:hover{background:#eef4fb;}' +
      'variant-selects select.rt-grouped option:checked{background:#11161c;color:#fff;}' +
      // hide the repeated rim on each ROW, keep it in the closed button's copy
      'variant-selects select.rt-grouped option .rt-rim{display:none;}' +
      'variant-selects select.rt-grouped selectedcontent .rt-rim{display:inline;}' +
      // mirror image of .rt-rim: the depth belongs on every ROW, but must stay OUT of the
      // closed control or the label re-wraps on a phone (the defect grouping exists to fix)
      'variant-selects select.rt-grouped option .rt-tread{display:inline;color:#5a6167;}' +
      'variant-selects select.rt-grouped selectedcontent .rt-tread{display:none;}' +
      'variant-selects:has(select.rt-grouped) .icon-caret{display:none;}' +
      '}';
    document.head.appendChild(st);
  }

  function groupHost(host) {
    var sels = host.querySelectorAll('select');
    if (sels.length !== 1) return;               // combined axis only
    var sel = sels[0], opts = [].slice.call(sel.options), i;
    if (!opts.length) return;
    var keep = sel.value;   // re-appending the options CLEARS selectedness - see the restore below

    var order = [], bucket = {};
    for (i = 0; i < opts.length; i++) {
      var at = opts[i].value.indexOf(SPLIT);
      if (at < 0) return;                        // not a combined axis - leave it alone
      var head = opts[i].value.slice(0, at);
      if (!bucket[head]) { bucket[head] = []; order.push(head); }
      bucket[head].push(opts[i]);
    }
    // RT 2026-08-29: a SINGLE-rim pack groups too. Without this its rows repeat the rim
    // on every line with no heading -- the noise the grouping exists to remove.
    if (!order.length) return;                   // nothing to group

    // IDEMPOTENCE: bail when the existing groups already match, or this mutates on every pass
    // and re-triggers the observer that called us ([[variant-hint-write-loop-flickers-cursor]]).
    var live = host.querySelectorAll('optgroup');
    if (live.length === order.length && (!CAN_STYLE || sel.querySelector('option .rt-rim'))) {
      var same = true;
      for (i = 0; i < live.length; i++) if (live[i].label !== order[i]) { same = false; break; }
      if (same) {
        // grouping is already correct; only the SELECTED row's label can still need a rewrite
        if (!CAN_STYLE) refreshLabels(sel);
        return;
      }
    }

    var frag = document.createDocumentFragment();
    for (i = 0; i < order.length; i++) {
      var g = document.createElement('optgroup');
      g.label = order[i];
      var rows = bucket[order[i]];
      for (var j = 0; j < rows.length; j++) {
        // The rim must stay in the option's MARKUP: the closed control shows the selected
        // option's content, so stripping it there leaves the buy box saying "215/50R17" with
        // no clue which rim ([[combined-axis-optgroup-grouping]]). Keep it in a span and hide
        // that span only INSIDE the open list, where the heading above already says it.
        if (CAN_STYLE && !rows[j].querySelector('.rt-rim')) {
          var cut = rows[j].value.indexOf(SPLIT) + SPLIT.length;
          var span = document.createElement('span');
          span.className = 'rt-rim';
          span.textContent = rows[j].value.slice(0, cut);
          rows[j].textContent = '';
          rows[j].appendChild(span);
          rows[j].appendChild(document.createTextNode(rows[j].value.slice(cut)));
          var dep = treadOf(rows[j].value);
          if (dep) {
            var ts = document.createElement('span');
            ts.className = 'rt-tread';
            ts.textContent = ' \u00b7 ' + dep;
            rows[j].appendChild(ts);
          }
        }
        g.appendChild(rows[j]);                  // moves the node, keeps value/selected
      }
      frag.appendChild(g);
    }
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    sel.appendChild(frag);
    sel.classList.add('rt-grouped');
    // The rim is redundant on every ROW (the heading above already says it) but must stay in the
    // CLOSED control. Authoring <button><selectedcontent></selectedcontent></button> gives the
    // closed display its own copy of the selected option, so CSS can hide .rt-rim in the list and
    // show it in the button. Only where the picker is customizable — a <button> inside a <select>
    // is not valid markup for a native picker, so never add it otherwise.
    if (!CAN_STYLE) refreshLabels(sel);
    if (CAN_STYLE && !sel.querySelector(':scope > button')) {
      var b = document.createElement('button');
      b.type = 'button';
      b.appendChild(document.createElement('selectedcontent'));
      sel.insertBefore(b, sel.firstChild);
    }
    injectStyle();
    // 🚨 Emptying the <select> DROPS the selection even though the same option nodes go
    // straight back in: measured live 2026-08-27, a picked "18x8 (+45mm) + 235/60R18" came back
    // as the LAST row, "19x8 (+45mm) + 245/55R19". The buy box would then price a wheel the
    // customer never chose. Restore it explicitly; conditional, so it stays idempotent.
    if (sel.value !== keep) sel.value = keep;
  }

  function fixHost(host) {
    var sels = [].slice.call(host.querySelectorAll('select'));
    if (sels.length < 2) return;                 // one axis can never hold a dead combination
    var data = variantsOf(host);
    if (!data) return;

    // GATE - a COMPLETE grid needs nothing done to it, whatever its size.
    var cells = 1, i;
    for (i = 0; i < sels.length; i++) cells *= sels[i].options.length;
    if (data.length >= cells) return;

    var picked = [];
    for (i = 0; i < sels.length; i++) picked.push(sels[i].value);

    for (i = 0; i < sels.length; i++) {
      var sel = sels[i], opts = [].slice.call(sel.options);
      var live = [], marked = [], deadSelected = false;

      for (var j = 0; j < opts.length; j++) {
        var o = opts[j], exists = false;

        for (var k = 0; k < data.length; k++) {
          if (data[k]['option' + (i + 1)] !== o.value) continue;
          var ok = true;
          // Constrain ONLY on the axes ABOVE this one - Dawn's own directional rule. Testing
          // against every axis greys the rim list too once an 18" rim is picked, and then
          // nothing can reach the 20" half of the listing.
          for (var p = 0; p < i; p++) {
            if (data[k]['option' + (p + 1)] !== picked[p]) { ok = false; break; }
          }
          if (ok) { exists = true; break; }
        }

        if (exists) {
          live.push(o);
          // Re-enable UNCONDITIONALLY. Dawn re-renders by patching the option TEXT and never
          // touches `disabled`, so a value we greyed in an earlier pass comes back with our
          // label gone but still disabled - and gating the undo on finding our own label left
          // the 18" tires permanently unselectable (live 2026-08-27).
          if (o.textContent.indexOf(HINT) > -1) setText(o, o.value);
          setFlag(o, 'disabled', false);
          continue;
        }

        var info = diameter(o.value);
        if (!info) continue;                       // cannot name the fix - leave Dawn's rendering
        setText(o, labelFor(o.value, info));
        setFlag(o, 'disabled', true);
        marked.push(o);
        if (o.selected) deadSelected = true;
      }

      // Never blank a whole axis: an axis with nothing selectable is worse than showing it all.
      if (!live.length) {
        for (var r = 0; r < marked.length; r++) {
          setText(marked[r], marked[r].value);
          setFlag(marked[r], 'disabled', false);
        }
        continue;
      }

      // The selection just became impossible - move to a real one so the price, the spec chips
      // and the quote buttons all describe something that exists.
      if (deadSelected && sel.value !== live[0].value) {
        var now = Date.now();
        burst = (now - lastAdvance < 1500) ? burst + 1 : 0;
        lastAdvance = now;
        if (burst > 4) return;    // every advance re-renders the picker; never spin the page
        sel.value = live[0].value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  /* ---- custom picker for browsers WITHOUT base-select (iOS Safari, Firefox) ----------
   * The native sheet's optgroup labels are small, grey and out of CSS reach; every in-control
   * trick (uppercase, unicode bold) was tried and rejected by RT 2026-08-27 ("still not good").
   * So sighted users get OUR panel — same look as the desktop base-select picker — while the
   * native select stays in the DOM, form-associated and focusable, as the assistive-tech path.
   * The proxy is aria-hidden and tabindex=-1 so AT never sees a duplicate control.
   * Everything here is observer-driven: every write is conditional or signature-gated
   * ([[variant-hint-write-loop-flickers-cursor]]).
   */
  function injectProxyStyle() {
    if (document.getElementById('rt-pack-proxy-style')) return;
    var st = document.createElement('style');
    st.id = 'rt-pack-proxy-style';
    st.textContent =
      '.rt-proxy-on{position:relative;}' +
      '.rt-proxy{flex:1 1 100%;}' +
      '.rt-proxy-on > select{position:absolute !important;opacity:0;width:1px;height:1px;' +
        'overflow:hidden;pointer-events:none;}' +
      '.rt-proxy-btn{display:flex;justify-content:space-between;align-items:center;width:100%;' +
        'font-size:15px;color:#1a1a1a;background:#fff;text-align:left;' +
        'border:0;background:transparent;padding:1.2rem 1.5rem;cursor:pointer;}' +
      '.rt-proxy-caret{margin-left:8px;flex:none;font-size:11px;color:#1a1a1a;}' +
      '.rt-proxy-panel{position:absolute;left:0;right:0;top:calc(100% + 4px);z-index:30;' +
        'background:#fff;border:1px solid #d7dbdf;border-radius:12px;' +
        'box-shadow:0 14px 36px rgba(0,0,0,.20);padding:6px;max-height:60vh;' +
        'overflow:auto;-webkit-overflow-scrolling:touch;}' +
      '.rt-proxy-head{font-size:17px;font-weight:800;color:#11161c;padding:12px 10px 6px;}' +
      '.rt-proxy-row{display:block;width:100%;text-align:left;font-size:15px;font-weight:500;' +
        'color:#1a1a1a;background:none;border:0;border-radius:8px;padding:10px 10px 10px 24px;' +
        'cursor:pointer;}' +
      '.rt-proxy-row.is-sel{background:#11161c;color:#fff;}';
    document.head.appendChild(st);
  }

  function proxySig(sel) {
    var parts = [sel.value];
    for (var i = 0; i < sel.options.length; i++) parts.push(sel.options[i].value);
    return parts.join('~');
  }

  function rebuildPanel(sel, panel) {
    while (panel.firstChild) panel.removeChild(panel.firstChild);
    var groups = sel.querySelectorAll('optgroup');
    for (var g = 0; g < groups.length; g++) {
      var head = document.createElement('div');
      head.className = 'rt-proxy-head';
      head.textContent = groups[g].label;
      panel.appendChild(head);
      var opts = groups[g].children;
      for (var i = 0; i < opts.length; i++) {
        var row = document.createElement('button');
        row.type = 'button';
        row.className = 'rt-proxy-row' + (opts[i].selected ? ' is-sel' : '');
        row.setAttribute('data-v', opts[i].value);
        var at = opts[i].value.indexOf(SPLIT);
        var dp = treadOf(opts[i].value);
        row.textContent = (at < 0 ? opts[i].value : opts[i].value.slice(at + SPLIT.length))
          + (dp ? ' \u00b7 ' + dp : '');
        panel.appendChild(row);
      }
    }
  }

  function proxyHost(host) {
    if (CAN_STYLE) return;
    var sels = host.querySelectorAll('select');
    if (sels.length !== 1) return;
    var sel = sels[0];
    if (!sel.querySelector('optgroup')) return;   // only the grouped combined-axis packs
    injectProxyStyle();
    var wrap = sel.parentElement;
    var proxy = wrap.querySelector(':scope > .rt-proxy');
    if (!proxy) {
      proxy = document.createElement('div');
      proxy.className = 'rt-proxy';
      proxy.setAttribute('aria-hidden', 'true');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rt-proxy-btn';
      btn.tabIndex = -1;
      btn.appendChild(document.createElement('span'));
      var caret = document.createElement('span');
      caret.className = 'rt-proxy-caret';
      caret.textContent = '\u25BC';
      btn.appendChild(caret);
      var panel = document.createElement('div');
      panel.className = 'rt-proxy-panel';
      panel.hidden = true;
      proxy.appendChild(btn);
      proxy.appendChild(panel);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        panel.hidden = !panel.hidden;
      });
      panel.addEventListener('click', function (e) {
        var row = e.target.closest ? e.target.closest('[data-v]') : null;
        if (!row) return;
        panel.hidden = true;
        if (sel.value !== row.getAttribute('data-v')) {
          sel.value = row.getAttribute('data-v');
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      document.addEventListener('click', function (e) {
        if (!proxy.contains(e.target)) panel.hidden = true;
      });
      wrap.appendChild(proxy);
      wrap.classList.add('rt-proxy-on');
    }
    // signature-gated sync: settles to zero writes, whatever re-rendered us
    var label = proxy.querySelector('.rt-proxy-btn > span');
    if (label.textContent !== sel.value) label.textContent = sel.value;
    var panelEl = proxy.querySelector('.rt-proxy-panel');
    var sig = proxySig(sel);
    if (panelEl.getAttribute('data-sig') !== sig) {
      rebuildPanel(sel, panelEl);
      panelEl.setAttribute('data-sig', sig);
    }
  }

  /* A pack whose values carry no ' + ' never groups (single rim, e.g. the Ram 1500 Snow Claw),
   * so the row-label paths above never run for it. Its picker is a plain native select, where
   * a <span> would not render as markup anyway — so write the text directly.
   * CONDITIONAL WRITE: `o.textContent !== want` is what stops the MutationObserver feeding
   * itself ([[variant-hint-write-loop-flickers-cursor]]). Never make this unconditional.
   * Only touches selects with no optgroups, so it can never fight groupHost/refreshLabels.
   */
  function treadPlain(host) {
    if (!treadMap()) return;
    var sels = host.querySelectorAll('select');
    if (sels.length !== 1) return;
    var sel = sels[0];
    if (sel.querySelector('optgroup')) return;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i], d = treadOf(o.value);
      var want = d ? o.value + ' \u00b7 ' + d : o.value;
      if (o.textContent !== want) o.textContent = want;
    }
  }

  function fix() {
    var hosts = document.querySelectorAll('variant-selects');
    for (var h = 0; h < hosts.length; h++) {
      try { groupHost(hosts[h]); } catch (e) { /* grouping is cosmetic - never fatal */ }
      try { treadPlain(hosts[h]); } catch (e) { /* depth is cosmetic - never fatal */ }
      try { proxyHost(hosts[h]); } catch (e) { /* the native select still works without it */ }
      try { fixHost(hosts[h]); } catch (e) { /* never take the page down over a picker */ }
    }
  }

  // Dawn replaces the picker markup on every change, so re-read the DOM rather than holding
  // references: the HOST survives, its children do not.
  document.addEventListener('change', function () { setTimeout(fix, 0); }, true);
  document.addEventListener('variant:change', function () { setTimeout(fix, 0); });

  function wire() {
    fix();
    var hosts = document.querySelectorAll('variant-selects');
    for (var h = 0; h < hosts.length; h++) {
      if (hosts[h].rtPackAxisObserved || !window.MutationObserver) continue;
      hosts[h].rtPackAxisObserved = true;
      new MutationObserver(function () { setTimeout(fix, 0); })
        .observe(hosts[h], { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
  window.addEventListener('load', wire);
})();
